import { FC, useState, useEffect } from "react";
import { Box, Button, Chip, Divider, Grid, InputAdornment, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { toast } from 'react-toastify';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { GstinSummary } from './GstinVerifyDialog';
import { useGstinVerify } from './GstinVerifyAdornment';
import CustomSelect, { IOptions } from "../../FormFields/CustomSelect";
import CustomTextField from '../../FormFields/CustomTextField';
import { useAppSelector } from "../../../store/hooks";
import { fetchStates } from '../../../store/stateStore/stateThunk';

interface LegalProps {
  selected: any;
  methods: any;
  isEdit?: boolean;
  /** Fields hidden by the account's form configuration. */
  hiddenFields?: string[];
  /** 'gstLookup' = only the GSTIN auto-fill card; 'withoutGst' = everything else. Default: all. */
  part?: 'all' | 'gstLookup' | 'withoutGst';
}

// Legal component
const Legal: FC<LegalProps> = ({ selected, methods, isEdit, hiddenFields = [], part = 'all' }) => {
  const hide = (field: string) => (hiddenFields.includes(field) ? 'none' : undefined);
  const [countryOptions, setCountryOptions] = useState<IOptions[]>([]);
  const [country, setCountry] = useState('');
  const [stateOptions, setStateOptions] = useState<IOptions[]>([]);
  const [legalState, setLegalState] = useState('');
  const watchedGstin = String(methods.watch ? methods.watch('taxIdentificationNumber2') || '' : '').trim().toUpperCase();
  const countriesList = useAppSelector(state => state.country.countries);
  const coutryStateList = useAppSelector(state => state.countryState.countryStates);

  useEffect(() => {
    console.log("debugging address data");
    console.log(selected);
    console.log(isEdit);
    console.log("debugging address data end");

    const setInitialData = () => {
      if (selected) {
        methods.setValue('registrationNo', selected?.registrationNo || '');

        // Handle both populated object and direct ObjectId string
        const countryId = typeof selected?.countryOfRegistration === 'string'
          ? selected?.countryOfRegistration
          : selected?.countryOfRegistration?._id || '';
        const stateId = typeof selected?.stateOfRegistration === 'string'
          ? selected?.stateOfRegistration
          : selected?.stateOfRegistration?._id || '';

        methods.setValue('countryOfRegistration', countryId);
        methods.setValue('stateOfRegistration', stateId);
        methods.setValue('taxIdentificationNumber1', selected?.taxIdentificationNumber1 || '');
        methods.setValue('taxIdentificationNumber2', selected?.taxIdentificationNumber2 || '');
        methods.setValue('openingBalanceAmount', selected?.openingBalance?.amount || '');
        methods.setValue('openingBalanceType', selected?.openingBalance?.balanceType || 'Dr');
        methods.setValue('openingBalanceDescription', selected?.openingBalance?.description || '');
        methods.setValue('openingBalanceReason', selected?.openingBalance?.reason || 'New books');
        methods.setValue('openingBalanceCustomReason', selected?.openingBalance?.customReason || '');
        setCountry(countryId);
        setLegalState(stateId);
      }
    }

    setInitialData();
    const countryOpts = countriesList.map((c: any) => {
      return { label: c.name, value: c._id }
    });
    setCountryOptions(countryOpts);
    // Auto-select first country on a new form, or on edit when none is saved
    if (countryOpts.length >= 1 && (!selected || (isEdit && !methods.getValues('countryOfRegistration')))) {
      setCountry(countryOpts[0].value);
      methods.setValue('countryOfRegistration', countryOpts[0].value);
    }
  }, [selected, countriesList, methods]);

  // States can arrive after countries; rebuild the options whenever they do.
  useEffect(() => {
    setStateOptions(coutryStateList.map((c: any) => ({ label: c.name, value: c._id })));
  }, [coutryStateList]);

  // GST state code (first 2 digits) matches State.tin, stored without a leading zero.
  const findStateId = (summary: GstinSummary) => {
    const code = parseInt(summary.stateCode || '', 10);
    const byTin = coutryStateList.find((st: any) => parseInt(st.tin, 10) === code);
    if (byTin) return byTin._id;
    const name = (summary.stateName || summary.address?.state || '').toLowerCase();
    const byName = name && coutryStateList.find((st: any) => st.name.toLowerCase().includes(name) || name.includes(st.name.toLowerCase()));
    return byName ? byName._id : '';
  };

  // Fill empty-or-overwrite rules: identity fields always, address fields always, display name only if blank.
  const applyVerified = (summary: GstinSummary) => {
    const set = (field: string, value: any) => methods.setValue(field, value ?? '', { shouldValidate: true, shouldDirty: true });
    // Business = GST trade name; Full Name = GST legal name (the owner, for proprietorships).
    if (summary.tradeName || summary.legalName) set('legalName', summary.tradeName || summary.legalName);
    const currentName = String(methods.getValues('name') || '').trim();
    if (!currentName && (summary.legalName || summary.tradeName)) set('name', summary.legalName || summary.tradeName);
    if (summary.pan) set('taxIdentificationNumber1', summary.pan);
    set('taxIdentificationNumber2', summary.gstin);
    const stateId = findStateId(summary);
    if (stateId) {
      setLegalState(stateId);
      set('stateOfRegistration', stateId);
      set('billingAddressState', stateId);
    }
    const a = summary.address || ({} as GstinSummary['address']);
    if (a.line1) set('billingAddressLine1', a.line1);
    if (a.line2) set('billingAddressLine2', a.line2);
    if (a.city) set('billingAddressCity', a.city);
    if (a.pincode) set('billingAddressPinCode', a.pincode);
  };

  const gstVerify = useGstinVerify({ value: watchedGstin, onVerified: applyVerified, onNormalize: (g) => methods.setValue('taxIdentificationNumber2', g) });

  // Keep the State of Registration dropdown in step when another part (GSTIN auto-fill) sets it.
  const watchedRegState = methods.watch ? methods.watch('stateOfRegistration') : undefined;
  useEffect(() => { if (watchedRegState) setLegalState(watchedRegState); }, [watchedRegState]);

  const gstField = (
    <>
      <h5 className='c-label'>GST Number</h5>
      <TextField
        placeholder='GST Number'
        className='c-text-field'
        {...methods.register('taxIdentificationNumber2')}
        variant="standard"
        sx={{ width: '100%' }}
        required
        size='small'
        inputProps={{ style: { textTransform: 'uppercase' } }}
        InputProps={{
          disableUnderline: true,
          readOnly: (selected && !isEdit) ? true : false,
          endAdornment: (!selected || isEdit) ? gstVerify.adornment : undefined
        }} />
      <p style={{ color: "#c33b3b" }}>{methods.formState.errors.taxIdentificationNumber2?.message ?? ""}</p>
    </>
  );

  if (part === 'gstLookup') {
    return (
      <Grid item xs={12} className="full-row">
        <Box sx={{ border: '1px solid #d1e7dd', bgcolor: '#f6fbf8', borderRadius: '10px', px: 2, py: 1.5 }}>
          <Grid container columnSpacing={2} rowSpacing={1} alignItems="center">
            <Grid item xs={12} md={6} className="full-row">
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <ReceiptLongOutlinedIcon sx={{ color: '#16a34a', mt: 0.25 }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1a1a1a' }}>Auto-fill from GSTIN</Typography>
                  <Typography sx={{ fontSize: '12.5px', color: '#6b7280' }}>
                    Enter the GST number and verify to fill name, PAN, state and billing address. Or skip and fill the form manually.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={gstVerify.captcha ? 3 : 6} className="full-row">{gstField}</Grid>
            {gstVerify.captcha && <Grid item xs={12} md={3} className="full-row">{gstVerify.captcha}</Grid>}
            {gstVerify.panel && <Grid item xs={12} className="full-row">{gstVerify.panel}</Grid>}
          </Grid>
          {gstVerify.dialog}
        </Box>
      </Grid>
    );
  }

  return (
    <>
      {/* <Grid item xs={12}>
    <Typography variant="body1">{'Registration & Legal'}</Typography>
    </Grid> */}
      <Grid item xs={12} mt={1}>
        <Divider style={{ marginRight: -8 }}>
          <Chip label="Registration & Legal" size="small" />
        </Divider>
      </Grid>
      <Grid item sx={{ display: hide('registrationNo') }} xs={4}>
        <h5 className='c-label'>Company Registration Number</h5>
        <TextField
          placeholder='Registration Number'
          className='c-text-field'
          {...methods.register('registrationNo')}
          variant="standard"
          sx={{ width: '100%' }}
          required
          size='small'
          InputProps={{
            disableUnderline: true,
            readOnly: (selected && !isEdit) ? true : false
          }} />
        <p style={{ color: "#c33b3b" }}>{methods.formState.errors.registrationNo?.message ?? ""}</p>

      </Grid>
      <Grid item sx={{ display: hide('stateOfRegistration') }} xs={4}>
        <h5 className='c-label'>State of Registration</h5>

        {
          <Select
            className='c-text-field'
            {...methods.register('stateOfRegistration')}
            disableUnderline
            required
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1.5px solid #e9ecf1",
                  // width: 255,
                  // marginLeft:'-29px !important',
                  boxShadow: 'none',
                  borderRadius: '5px'
                }
              }
            }}
            style={{ width: '100%' }}
            value={legalState ? legalState : ''}
            disabled={(selected && !isEdit) ? true : false}
            onChange={(event: SelectChangeEvent<any>) => {
              setLegalState(event.target.value);
              methods.setValue('stateOfRegistration', event.target.value);
            }}
            size='small'
            variant='outlined'
            sx={{
              boxShadow: "none",
              // fontSize:12,
              textAlign: 'right',
              '& .MuiOutlinedInput-input': { p: 0.5 },
              // '& .MuiSelect-select': { p:0 },
              marginBottom: '3px',
              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
              {
                border: 0,
              },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: 0,
              },
            }}
          >
            {stateOptions && stateOptions.map((option) =>
              <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
            )}
          </Select>}
        <p style={{ color: "#c33b3b" }}>{methods.formState.errors.stateOfRegistration?.message ?? ""}</p>
      </Grid>
      <Grid item sx={{ display: hide('countryOfRegistration') }} xs={4}>
        <h5 className='c-label'>Country of registration</h5>
        {countryOptions?.length > 0 && <Select
          className='c-text-field'
          {...methods.register('countryOfRegistration')}
          disableUnderline
          required
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1.5px solid #e9ecf1",
                // width: 255,
                // marginLeft:'-29px !important',
                boxShadow: 'none',
                borderRadius: '5px'
              }
            }
          }}
          style={{ width: '100%' }}
          value={country ? country : ''}
          disabled={(selected && !isEdit) ? true : false}
          onChange={(event: SelectChangeEvent<any>) => {
            setCountry(event.target.value);
            methods.setValue('countryOfRegistration', event.target.value);
          }}
          size='small'
          variant='outlined'
          sx={{
            boxShadow: "none",
            // fontSize:12,
            textAlign: 'right',
            '& .MuiOutlinedInput-input': { p: 0.5 },
            // '& .MuiSelect-select': { p:0 },
            marginBottom: '3px',
            ".MuiOutlinedInput-notchedOutline": { border: 0 },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
            {
              border: 0,
            },
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              border: 0,
            },
          }}
        >
          {countryOptions && countryOptions.map((option) =>
            <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
          )}
        </Select>}
        <p style={{ color: "#c33b3b" }}>{methods.formState.errors.countryOfRegistration?.message ?? ""}</p>
      </Grid>
      <Grid item sx={{ display: hide('taxIdentificationNumber1') }} xs={4}>
        <h5 className='c-label'>PAN Number</h5>
        <TextField
          placeholder='PAN Number'
          className='c-text-field'
          {...methods.register('taxIdentificationNumber1')}
          variant="standard"
          sx={{ width: '100%' }}
          required
          size='small'
          InputProps={{
            disableUnderline: true,
            readOnly: (selected && !isEdit) ? true : false
          }} />
        <p style={{ color: "#c33b3b" }}>{methods.formState.errors.taxIdentificationNumber1?.message ?? ""}</p>
      </Grid>
      {part !== 'withoutGst' && <>
      <Grid item xs={4}>{gstField}</Grid>
      {/* Captcha for the GST portal: image beside the GST field, code box and buttons on the next row */}
      {gstVerify.captcha && <Grid item xs={4}>{gstVerify.captcha}</Grid>}
      {gstVerify.panel && <Grid item xs={12}>{gstVerify.panel}</Grid>}
      {gstVerify.dialog}
      </>}

      {/* Opening Balance Section */}
      <Grid item sx={{ display: hide('openingBalance') }} xs={12} mt={1}>
        <Divider style={{ marginRight: -8 }}>
          <Chip label="Opening Balance" size="small" />
        </Divider>
      </Grid>

      <Grid item sx={{ display: hide('openingBalance') }} xs={3}>
        <h5 className='c-label'>Opening Balance Amount</h5>
        <TextField
          placeholder='Opening Balance Amount'
          className='c-text-field'
          {...methods.register('openingBalanceAmount')}
          variant="standard"
          type='number'
          sx={{ width: '100%' }}
          size='small'
          InputProps={{
            disableUnderline: true,
            readOnly: (selected && !isEdit) ? true : false
          }} />
        <p style={{ color: "#c33b3b" }}>{methods.formState.errors.openingBalanceAmount?.message ?? ""}</p>
        {methods.watch('openingBalanceAmount') && Number(methods.watch('openingBalanceAmount')) > 0 && (
          <p style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
            {methods.watch('openingBalanceType') === 'Dr'
              ? 'Party owes you (Receivable)'
              : 'You owe party (Payable)'}
          </p>
        )}
      </Grid>
      <Grid item sx={{ display: hide('openingBalance') }} xs={3}>
        <h5 className='c-label'>Balance Type</h5>
        <Select
          className='c-text-field'
          {...methods.register('openingBalanceType')}
          disableUnderline
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1.5px solid #e9ecf1",
                boxShadow: 'none',
                borderRadius: '5px'
              }
            }
          }}
          style={{ width: '100%' }}
          value={methods.watch('openingBalanceType') || 'Dr'}
          disabled={(selected && !isEdit) ? true : false}
          onChange={(event: SelectChangeEvent<any>) => {
            methods.setValue('openingBalanceType', event.target.value);
          }}
          size='small'
          variant='outlined'
          sx={{
            boxShadow: "none",
            textAlign: 'right',
            '& .MuiOutlinedInput-input': { p: 0.5 },
            marginBottom: '3px',
            ".MuiOutlinedInput-notchedOutline": { border: 0 },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
            {
              border: 0,
            },
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              border: 0,
            },
          }}
        >
          <MenuItem value="Dr">Debit (Dr)</MenuItem>
          <MenuItem value="Cr">Credit (Cr)</MenuItem>
        </Select>
        <p style={{ color: "#c33b3b" }}>{methods.formState.errors.openingBalanceType?.message ?? ""}</p>
      </Grid>
      <Grid item sx={{ display: hide('openingBalance') }} xs={3}>
        <h5 className='c-label'>Opening Balance Description</h5>
        <TextField
          placeholder='Opening Balance Description'
          className='c-text-field'
          {...methods.register('openingBalanceDescription')}
          variant="standard"
          sx={{ width: '100%' }}
          size='small'
          InputProps={{
            disableUnderline: true,
            readOnly: (selected && !isEdit) ? true : false
          }} />
        <p style={{ color: "#c33b3b" }}>{methods.formState.errors.openingBalanceDescription?.message ?? ""}</p>
      </Grid>
      <Grid item sx={{ display: hide('openingBalance') }} xs={3}>
        <h5 className='c-label'>Opening Balance Reason</h5>
        <Select
          className='c-text-field'
          {...methods.register('openingBalanceReason')}
          disableUnderline
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1.5px solid #e9ecf1",
                boxShadow: 'none',
                borderRadius: '5px'
              }
            }
          }}
          style={{ width: '100%' }}
          value={methods.watch('openingBalanceReason') || 'New books'}
          disabled={(selected && !isEdit) ? true : false}
          onChange={(event: SelectChangeEvent<any>) => {
            methods.setValue('openingBalanceReason', event.target.value);
          }}
          size='small'
          variant='outlined'
          sx={{
            boxShadow: "none",
            textAlign: 'right',
            '& .MuiOutlinedInput-input': { p: 0.5 },
            marginBottom: '3px',
            ".MuiOutlinedInput-notchedOutline": { border: 0 },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
            {
              border: 0,
            },
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              border: 0,
            },
          }}
        >
          <MenuItem value="New books">New Books</MenuItem>
          <MenuItem value="Previous year">Previous Year</MenuItem>
          <MenuItem value="Audited">Audited</MenuItem>
          <MenuItem value="Books were lost">Books Were Lost</MenuItem>
          <MenuItem value="Data corruption">Data Corruption</MenuItem>
          <MenuItem value="Past records unavailable">Past Records Unavailable</MenuItem>
          <MenuItem value="Custom">Custom</MenuItem>
        </Select>
        <p style={{ color: "#c33b3b" }}>{methods.formState.errors.openingBalanceReason?.message ?? ""}</p>
      </Grid>
      {methods.watch('openingBalanceReason') === 'Custom' && (
        <Grid item sx={{ display: hide('openingBalance') }} xs={6} mb={4}>
          <h5 className='c-label'>Custom Reason</h5>
          <TextField
            placeholder='Enter custom reason'
            className='c-text-field'
            {...methods.register('openingBalanceCustomReason')}
            variant="standard"
            sx={{ width: '100%' }}
            size='small'
            InputProps={{
              disableUnderline: true,
              readOnly: (selected && !isEdit) ? true : false
            }} />
          <p style={{ color: "#c33b3b" }}>{methods.formState.errors.openingBalanceCustomReason?.message ?? ""}</p>
        </Grid>
      )}
    </>
  );
};

export default Legal;
